import { Dynamic } from "solid-js/web";
import { cn } from "@mealplan/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Heading semantics for the `lg` wordmark. Default "h1": the logo is the page
   * heading. Pass "p" when a sibling already owns the page h1 (e.g. the auth
   * card carries the h1 and the brand surface is decorative).
   */
  as?: "h1" | "p";
}

/**
 * Marca propia de Mealplan: un plato visto desde arriba con cubiertos (tenedor +
 * cuchara) y una hoja de hierba fresca, sobre un "calendario de comidas" sutil.
 * Mark DISTINTIVO del producto (NO el CheckSquare genérico del template).
 *
 * GOTCHA REAL (confirmado): dibujar el mark con `stroke-accent`/`fill-accent` o
 * cualquier color de marca sobre una superficie tenue lo vuelve INVISIBLE en
 * varios temas (p.ej. ámbar/slate/indigo de los ~16 seeds), porque cada seed
 * sobreescribe `--accent` a un color arbitrario que puede chocar con el fondo.
 *
 * FIX: el mark se dibuja con `currentColor`, heredado de un chip SÓLIDO
 * `bg-accent` + `text-accent-foreground`. Ese par tiene contraste GARANTIZADO
 * por el motor de temas (es el mismo par que usan los botones primarios), así
 * que el mark es legible en CADA tema (base light/dark/initialization + seeds).
 * El wordmark usa `text-foreground` (contraste con el fondo, validado por tema).
 */
function LogoMark(props: { class?: string }) {
  return (
    <span
      class={cn(
        "grid shrink-0 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5",
        props.class,
      )}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        class="h-[68%] w-[68%]"
      >
        {/* Calendario de comidas (sutil, al fondo) */}
        <rect
          x="5"
          y="6"
          width="22"
          height="21"
          rx="3.5"
          stroke="currentColor"
          stroke-width="1.6"
          opacity="0.45"
        />
        <path d="M5 11.5h22" stroke="currentColor" stroke-width="1.6" opacity="0.45" />
        <path
          d="M11 4.5v3.5M21 4.5v3.5"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          opacity="0.45"
        />
        {/* Plato */}
        <circle cx="16" cy="19" r="6.4" stroke="currentColor" stroke-width="1.7" />
        <circle cx="16" cy="19" r="3" stroke="currentColor" stroke-width="1.3" opacity="0.55" />
        {/* Tenedor */}
        <g stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
          <path d="M10 14v2.6" />
          <path d="M12 14v2.6" />
          <path d="M11 16.6V25" />
        </g>
        {/* Cuchara */}
        <path
          d="M21 14.4c1.5 0 2.4 1.2 2.4 2.6 0 1.2-.8 2.1-2 2.3M21.4 19.3 21 25"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-9 w-9" />
          <span class="font-serif text-2xl font-semibold tracking-tight text-foreground">
            Meal<span class="text-accent">plan</span>
          </span>
        </>
      ) : (
        <>
          <LogoMark class="mb-1 h-16 w-16 transition-transform duration-300 hover:scale-105 active:scale-95" />
          <Dynamic
            component={props.as ?? "h1"}
            class="font-serif text-3xl font-semibold tracking-tight text-foreground"
          >
            Meal<span class="text-accent">plan</span>
          </Dynamic>
        </>
      )}
    </div>
  );
}
